import React, { useState, useEffect } from "react";

function VisitsReports({ schedules, qaUsers, centers, onDownloadReport }) {
  const [filters, setFilters] = useState({
    user: "ALL",
    status: "ALL",
    fromDate: "",
    toDate: "",
  });

  const [filteredSchedules, setFilteredSchedules] = useState([]);

  // Apply filters
  useEffect(() => {
    let filtered = [...schedules];

    // Filter by QA User
    if (filters.user !== "ALL") {
      filtered = filtered.filter(s => s.qaUserId === filters.user);
    }

    // Filter by Status
    if (filters.status !== "ALL") {
      filtered = filtered.filter(s => s.status === filters.status);
    }

    // Filter by Date Range
    if (filters.fromDate) {
      filtered = filtered.filter(s => {
        const visitDate = new Date(s.visitDate);
        const fromDate = new Date(filters.fromDate);
        return visitDate >= fromDate;
      });
    }

    if (filters.toDate) {
      filtered = filtered.filter(s => {
        const visitDate = new Date(s.visitDate);
        const toDate = new Date(filters.toDate);
        return visitDate <= toDate;
      });
    }

    setFilteredSchedules(filtered);
  }, [schedules, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      user: "ALL",
      status: "ALL",
      fromDate: "",
      toDate: "",
    });
  };

  const getCenterName = (centerId) => {
    const center = centers.find(c => c.id === centerId);
    return center ? center.name : "Unknown Center";
  };

  const getCityName = (centerId) => {
    const center = centers.find(c => c.id === centerId);
    return center ? center.city : "Unknown City";
  };

  const getUserName = (userId) => {
    const user = qaUsers.find(u => String(u.id) === String(userId));
    return user ? `${user.firstName} ${user.lastName}` : "Unknown User";
  };

  const getStatusBadge = (status) => {
    return (
      <span className="pill" style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '.35rem',
        padding: '.18rem .65rem',
        borderRadius: '999px',
        fontSize: '.72rem',
        fontWeight: 700,
        backgroundColor: status === "Pending" ? '#fff3cd' : 
                        status === "In Progress" ? '#cfe2ff' : 
                        status === "Completed" ? '#d4edda' : 
                        status === "Missed" ? '#f8d7da' : '#f3f5f7',
        color: status === "Pending" ? '#856404' : 
               status === "In Progress" ? '#084298' : 
               status === "Completed" ? '#155724' : 
               status === "Missed" ? '#842029' : '#6c757d'
      }}>
        {status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="row g-3">
      {/* Left Column - Filters & Legend */}
      <div className="col-lg-3">
        {/* Filters */}
        <div className="form-section" style={{ 
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '1rem 1.25rem',
          background: 'white',
          marginBottom: '1rem'
        }}>
          <div className="form-section-title" style={{ 
            fontWeight: 700,
            fontSize: '.95rem',
            marginBottom: '.75rem'
          }}>
            Filters
          </div>
          
          <div className="mb-2">
            <label className="form-label small" style={{ fontSize: '.875rem' }}>
              QA User
            </label>
            <select
              value={filters.user}
              onChange={(e) => handleFilterChange("user", e.target.value)}
              className="form-select form-select-sm"
              style={{
                display: 'block',
                width: '100%',
                padding: '0.375rem 0.75rem',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: '#212529',
                backgroundColor: '#fff',
                backgroundClip: 'padding-box',
                border: '1px solid #ced4da',
                borderRadius: '0.25rem'
              }}
            >
              <option value="ALL">All QA Users</option>
              {qaUsers.map(user => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-2">
            <label className="form-label small" style={{ fontSize: '.875rem' }}>
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="form-select form-select-sm"
              style={{
                display: 'block',
                width: '100%',
                padding: '0.375rem 0.75rem',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: '#212529',
                backgroundColor: '#fff',
                backgroundClip: 'padding-box',
                border: '1px solid #ced4da',
                borderRadius: '0.25rem'
              }}
            >
              <option value="ALL">All</option>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Missed">Missed</option>
            </select>
          </div>

          <div className="mb-2">
            <label className="form-label small" style={{ fontSize: '.875rem' }}>
              From Date
            </label>
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange("fromDate", e.target.value)}
              className="form-control form-control-sm"
              style={{
                display: 'block',
                width: '100%',
                padding: '0.375rem 0.75rem',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: '#212529',
                backgroundColor: '#fff',
                backgroundClip: 'padding-box',
                border: '1px solid #ced4da',
                borderRadius: '0.25rem'
              }}
            />
          </div>

          <div className="mb-2">
            <label className="form-label small" style={{ fontSize: '.875rem' }}>
              To Date
            </label>
            <input
              type="date"
              value={filters.toDate}
              onChange={(e) => handleFilterChange("toDate", e.target.value)}
              className="form-control form-control-sm"
              style={{
                display: 'block',
                width: '100%',
                padding: '0.375rem 0.75rem',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: '#212529',
                backgroundColor: '#fff',
                backgroundClip: 'padding-box',
                border: '1px solid #ced4da',
                borderRadius: '0.25rem'
              }}
            />
          </div>

          <button
            onClick={resetFilters}
            className="btn btn-outline-secondary btn-sm w-100"
          >
            <i className="fas fa-eraser mr-2"></i>
            Reset Filters
          </button>
        </div>

        {/* Legend */}
        <div className="form-section" style={{ 
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '1rem 1.25rem',
          background: 'white',
          marginBottom: '1rem'
        }}>
          <div className="form-section-title" style={{ 
            fontWeight: 700,
            fontSize: '.95rem',
            marginBottom: '.75rem'
          }}>
            Status Legend
          </div>
          
          <div className="small d-flex flex-column gap-2" style={{ fontSize: '.875rem' }}>
            <div>
              <span className="pill" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '.35rem',
                padding: '.18rem .65rem',
                borderRadius: '999px',
                fontSize: '.72rem',
                fontWeight: 700,
                backgroundColor: '#fff3cd',
                color: '#856404'
              }}>
                Pending
              </span> not started
            </div>
            <div>
              <span className="pill" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '.35rem',
                padding: '.18rem .65rem',
                borderRadius: '999px',
                fontSize: '.72rem',
                fontWeight: 700,
                backgroundColor: '#cfe2ff',
                color: '#084298'
              }}>
                In Progress
              </span> time-in captured
            </div>
            <div>
              <span className="pill" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '.35rem',
                padding: '.18rem .65rem',
                borderRadius: '999px',
                fontSize: '.72rem',
                fontWeight: 700,
                backgroundColor: '#d4edda',
                color: '#155724'
              }}>
                Completed
              </span> PDF available
            </div>
            <div>
              <span className="pill" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '.35rem',
                padding: '.18rem .65rem',
                borderRadius: '999px',
                fontSize: '.72rem',
                fontWeight: 700,
                backgroundColor: '#f8d7da',
                color: '#842029'
              }}>
                Missed
              </span> visit date passed
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Visits Table */}
      <div className="col-lg-9">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0" style={{ fontSize: '1rem', fontWeight: 600 }}>
            All QA Visits ({filteredSchedules.length})
          </h6>
          
          <div className="small text-muted" style={{ fontSize: '.875rem' }}>
            <i className="fas fa-info-circle mr-1"></i>
            Demo: PDF download generates a sample file on your browser (not real report)
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th style={{ width: '42px', fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>#</th>
                <th style={{ width: '120px', fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>Schedule ID</th>
                <th style={{ width: '160px', fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>QA User</th>
                <th style={{ fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>Center</th>
                <th style={{ width: '140px', fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>City</th>
                <th style={{ width: '130px', fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>Visit Date</th>
                <th style={{ width: '145px', fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>Actual Visit Date</th>
                <th style={{ width: '120px', fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>Status</th>
                <th style={{ width: '140px', fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>Download PDF</th>
              </tr>
            </thead>
            
            <tbody>
              {filteredSchedules.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center text-muted py-4">
                    No visits found
                  </td>
                </tr>
              ) : (
                filteredSchedules.map((schedule, index) => (
                  <tr key={schedule.id}>
                    <td style={{ fontSize: '.875rem', color: '#6c757d' }}>
                      {index + 1}
                    </td>
                    <td style={{ 
                      fontSize: '.875rem', 
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                    }}>
                      {schedule.id}
                    </td>
                    <td style={{ fontSize: '.875rem', fontWeight: 500 }}>
                      {getUserName(schedule.qaUserId)}
                    </td>
                    <td style={{ fontSize: '.875rem' }}>
                      {getCenterName(schedule.centerId)}
                    </td>
                    <td style={{ fontSize: '.875rem', color: '#6c757d' }}>
                      {getCityName(schedule.centerId)}
                    </td>
                    <td style={{ 
                      fontSize: '.875rem', 
                      color: '#6c757d',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                    }}>
                      {schedule.visitDate}
                    </td>
                    <td style={{ 
                      fontSize: '.875rem', 
                      color: '#6c757d',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                    }}>
                      {schedule.actualVisitDate || (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {getStatusBadge(schedule.status)}
                    </td>
                    <td>
                      {schedule.status === "Completed" ? (
                        <button
                          onClick={() => onDownloadReport(schedule)}
                          className="btn btn-outline-danger btn-sm"
                        >
                          <i className="fas fa-file-pdf mr-1"></i>
                          Download
                        </button>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Summary Stats */}
        {/* <div className="row g-2 mt-4">
          <div className="col-3">
            <div className="kpi" style={{
              border: '1px solid #cfe2ff',
              borderRadius: '10px',
              background: '#d4edda',
              padding: '.85rem 1rem',
              height: '100%'
            }}>
              <div className="value" style={{ 
                fontWeight: 800, 
                fontSize: '1.25rem',
                color: '#155724'
              }}>
                {filteredSchedules.length}
              </div>
              <div className="label" style={{ 
                color: '#155724', 
                fontSize: '.8rem' 
              }}>
                Total Visits
              </div>
            </div>
          </div>
          
          <div className="col-3">
            <div className="kpi" style={{
              border: '1px solid #d4edda',
              borderRadius: '10px',
              background: '#d4edda',
              padding: '.85rem 1rem',
              height: '100%'
            }}>
              <div className="value" style={{ 
                fontWeight: 800, 
                fontSize: '1.25rem',
                color: '#155724'
              }}>
                {filteredSchedules.filter(s => s.status === "Completed").length}
              </div>
              <div className="label" style={{ 
                color: '#155724', 
                fontSize: '.8rem' 
              }}>
                Completed
              </div>
            </div>
          </div>
          
          <div className="col-3">
            <div className="kpi" style={{
              border: '1px solid #fff3cd',
              borderRadius: '10px',
              background: '#fff3cd',
              padding: '.85rem 1rem',
              height: '100%'
            }}>
              <div className="value" style={{ 
                fontWeight: 800, 
                fontSize: '1.25rem',
                color: '#856404'
              }}>
                {filteredSchedules.filter(s => s.status === "Pending").length}
              </div>
              <div className="label" style={{ 
                color: '#856404', 
                fontSize: '.8rem' 
              }}>
                Pending
              </div>
            </div>
          </div>
          
          <div className="col-3">
            <div className="kpi" style={{
              border: '1px solid #f8d7da',
              borderRadius: '10px',
              background: '#f8d7da',
              padding: '.85rem 1rem',
              height: '100%'
            }}>
              <div className="value" style={{ 
                fontWeight: 800, 
                fontSize: '1.25rem',
                color: '#842029'
              }}>
                {filteredSchedules.filter(s => s.status === "Missed").length}
              </div>
              <div className="label" style={{ 
                color: '#842029', 
                fontSize: '.8rem' 
              }}>
                Missed
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
}

export default VisitsReports;